import { html, css, LitElement } from 'lit';
import { customElement, state  } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '@sdb-develop/learning-composition-editor/src/store/store.js';
import sharedStyles from '@sdb-develop/learning-composition-editor/src/app/styles.js';
import { colors } from '@sdb-develop/learning-frontend/dist/src/sdb-colors.js';
import { loadUserSettings } from '@sdb-develop/learning-composition-editor/src/store/env/env.js';
import { translate } from '@sdb-develop/learning-composition-editor/src/helpers/translate.js';
import { applyStyles } from '@sdb-develop/learning-composition-editor/src/helpers/stylesheets.js';
import '@sdb-develop/learning-frontend';
import '@sdb-develop/learning-composition-editor/src/components/editor/sdb-embedded-composition-editor.js';
import '@sdb-develop/learning-composition-editor/src/components/preview/sdb-embedded-preview-dialog.js';
import '@sdb-develop/learning-composition-editor/src/components/shared/sdb-loading.js';
import '@sdb-develop/learning-composition-editor/src/components/shared/sdb-overlay-element.js';
import '@sdb-develop/learning-composition-editor/src/components/shared/sdb-status-message.js';

@customElement('sdb-composition-editor-app')
class SdbCompositionEditorSdb extends connect(store)(LitElement) {

    @state()
    accessor isLoading;

    @state()
    accessor menuItems = [];

    @state()
    accessor error;

    stateChanged(state) {
        this.isLoading = state.env.status === 'loading';
        this.menuItems = state.env.userSettings?.menuItems || [];
        this.userName = state.auth.user?.profile?.name || '---';
        this.error = state.env.status === 'failed';
    }

    static get themeStyles() {
        return css`
            body {
                --tcg-list-bottom-margin: 80px;
                --tcg-content-max-width: 1400px;
                --tcg-list-padding: 24px;
                --tcg-input-padding-bottom: 24px;
                --icon-button-color: rgba(0,0,0, var(--dark-secondary-opacity));
                --tcg-input-container-background-color: hsl(0, 0%, 96%);
                --tcg-input-container-border-radius: 4px 4px 0 0;
                --tcg-minerva-green: rgb(102, 153, 102); /* #669966 */
                --tcg-minerva-green-background: rgba(102, 153, 102, 0.2);
                --tcg-minerva-green-dark: rgb(72, 109, 72); /* #486D48 */
                --tcg-minerva-red: rgb(204, 102, 102); /* #CC6666 */
                --tcg-minerva-red-background: rgba(204, 102, 102, 0.2);
                --tcg-minerva-red-dark: rgb(153, 51, 51); /* #993333 */
                --tcg-minerva-orange: rgb(204, 153, 0); /* #CC9900 */
                --tcg-minerva-orange-background: rgba(204, 153, 0, 0.2);
                --tcg-minerva-orange-dark: rgb(188, 134, 0); /* #BC8600 */
                --tcg-black-primary-opacity: rgba(0, 0, 0, var(--dark-primary-opacity));
                --tcg-minerva-gray: rgba(0, 0, 0, 0.04);
                --sdb-primary-button: #f49d10;
                --mdc-theme-primary: var(--tcg-primary-background-color);
                --mdc-theme-secondary: var(--tcg-secondary-background-color);
                --mdc-theme-on-secondary: var(--tcg-primary-color-onsecondary);

                /* MAIN */
                --body: #F7F7F7;
                --theme-primary: var(--sdb-theme-primary, #00b1aa);
                --theme-secondary: var(--sdb-theme-secondary, #1e958c);
                --theme-tertiary: var(--sdb-theme-tertiary, #1f9bb4);
                /* PROGRESS BAR */
                --progress-primary:#f49d10;
                --progress-secondary:#033850;
                --progress-tertiary:#CC6666;
                --progress-100:#00b1aa;
                /* ELEMENT COLORS */
                --element-lightgrey: #eaeaea;
                --element-grey: #dddedf;
                --element-darkgrey: #cacaca;
                --element-disabled: #a0a0a0;
                /* HEADER */
                --header-primary: var(--sdb-header-primary, #00b1aa);
                --header-secondary: var(--sdb-header-primary, #136f8b);
                --header-tertiary: var(--sdb-header-primary, #b2b2b2);
                /* TEXT */
                --text-primary: #023850;
                --text-light: #ffffff;
                --text-dark: #023850;
                font-family: Source Sans Pro, sans-serif;
                --font-family: Source Sans Pro, sans-serif;
                --font-default: 400 16px Source Sans Pro, sans-serif;
                /* DIALOG */
                --warning-color: #f49d10;
                --warning-background: #F3E8D6;
                --alert-color: #f41710;
                --alert-background: #F3D2D1;
                --success-color: #00b1aa;
                --success-background: #CFE9E8;
                /* BUTTONS */
                --btn-primary: var(--sdb-button-primary, #f49d10);
                --btn-primary-hover: var(--sdb-button-primary-hover, #ed7422);
                --btn-secondary: var(--sdb-button-secondary, #023850);
                --btn-secondary-hover: var(--sdb-button-secondary, #013247);
                --btn-secondary-text: var(--sdb-button-secondary-text, #ffffff);
                --btn-disabled: #eee;

                --paper-input-container: {
                    --paper-input-container-color: hsl(0, 0%, 58%);
                    background-color: var(--tcg-input-container-background-color);
                    border-radius: var(--tcg-input-container-border-radius);
                    padding-bottom:0;
                    padding-left: 12px;
                    padding-right: 12px;
                }

                --paper-input-container-underline: {
                    /*
                    undo padding-left and padding-right of --paper-input-container
                    this makes sure that the underline runs across the whole input container
                    */
                    margin-left: -12px;
                    margin-right: -12px;
                }

                --paper-input-container-underline-focus: {
                    /*
                    undo padding-left and padding-right of --paper-input-container
                    this makes sure that the underline runs across the whole input container
                    */
                    margin-left: -12px;
                    margin-right: -12px;
                }

                --iron-autogrow-textarea: {
                    padding:0; /* Browser stylesheet defaults to 2px padding, makes this misalign with the label when using paper-textarea. */
                }

                --iron-overlay-backdrop-opacity: 0.54;
                --tcg-primary-color-onsecondary: rgba(255,255,255,.7);

                paper-toast.toast {
                    text-align: center;
                }

                vaadin-dialog-overlay::part(overlay) {
                    max-width: 100%;
                }
            }
        `;
    }

    static get styles() {
        return css`
            .app-header {
            }

            .app-bar {
                background: #fff;
                height: 70px;
                padding-left: 10%;
                padding-right: 10%;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .title {
                background: #fff;
                height: 70px;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .logo img {
                height: 40px;
            }

            .title-text {
                font-size: 20px;
                margin-left: 20px;
            }

            .menu-bar {
                position: relative;
                height: 70px;
                display: flex;
                align-items: center;
            }

            #background {
                background:var(--theme-primary);
                position: absolute;
                z-index: -1;
            }

            .menu {
                padding-left: 10%;
                padding-right: 10%;
                display: flex;
                align-items: center;
            }

            a.menu-item {
                display: inline-block;
                font-size: 16px;
                transition: all .1s ease-in-out;
                color: rgba(255,255,255,.7);
                text-align: center;
                padding: 0 20px;
                text-decoration: none;
            }

            a.menu-item:first-child {
                padding-left: 0;
            }

            a.menu-item:hover {
                transform: scale(1.05);
                color: rgba(255,255,255,.8);
            }

            span.separator {
                display: inline-block;
                width: 0px;
                height: 20px;
                border-right: 1.5px solid rgba(255,255,255,.7);
            }

            [hidden] { /* override constructed styles */
                display: none !important;
            }

            div.message-container {
                position: absolute;
                top:0;
                left:0;
                right:0;
                bottom:0;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        `;
    }

    get appTitle() {
        let title = translate('app');

        switch (window.ENVIRONMENT) {
            case 'development': return `${title} (DEV)`;
            case 'production': return title;
            default: return `${title} (DEBUG)`;
        }
    }

    connectedCallback() {
        super.connectedCallback();
        document.title = this.appTitle;
    }

    render() {
        if (this.isLoading) {
            return html`<sdb-loading></sdb-loading>`;
        }

        if (this.error) {
            return html`
                <div class="message-container app-theme-container">
                    <sdb-status-message
                        label=${translate('connectionError')}>
                    </sdb-status-message>
                </div>
            `;
        }

        if (this.menuItems.length === 0) {
            return html`
                <div class="message-container app-theme-container">
                    <sdb-status-message
                        label=${translate('accessDenied')}>
                    </sdb-status-message>
                </div>
            `;
        }

        return html`
            <div class="app-header app-theme-container">
                <div class="app-bar">
                    <div class="title">
                        <div class="logo">
                            <img src="../../images/sdb.jpeg">
                        </div>
                        <div class="title-text">${this.appTitle}</div>
                    </div>
                    <div>${this.userName}</div>
                </div>
                <div class="menu-bar">
                    <svg
                        id="background"
                        viewBox="0 0 500 150"
                        preserveAspectRatio="none"
                        style="height: 100%; width: 100%;">
                        <path
                            d="M0,100 C150,300 450,0 450,0 L500,00 L0,0 Z"
                            style="stroke: none;fill: var(--theme-secondary);">
                        </path>
                    </svg>
                    <div class="menu">
                        <a
                            class="menu-item"
                            href="drafts"
                            ?hidden=${!this.menuItems.includes('drafts')}>${translate('menuDrafts')}</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('compositions')}></span>
                        <a
                            class="menu-item"
                            href="compositions"
                            ?hidden=${!this.menuItems.includes('compositions')}>${translate('menuPublished')}</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('lti')}></span>
                        <a
                            class="menu-item"
                            href="lti"
                            ?hidden=${!this.menuItems.includes('lti')}>LTI 1.1</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('ltiAdvantage')}></span>
                        <a
                            class="menu-item"
                            href="lti-advantage"
                            ?hidden=${!this.menuItems.includes('ltiAdvantage')}>LTI 1.3</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('scorm')}></span>
                        <a
                            class="menu-item"
                            href="scorm"
                            ?hidden=${!this.menuItems.includes('scorm')}>SCORM</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('os')}></span>
                        <a
                            class="menu-item"
                            href="os"
                            ?hidden=${!this.menuItems.includes('os')}>Online Studio</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('youtube')}></span>
                        <a
                            class="menu-item"
                            href="youtube"
                            ?hidden=${!this.menuItems.includes('youtube')}>YouTube</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('vimeo')}></span>
                        <a
                            class="menu-item"
                            href="vimeo"
                            ?hidden=${!this.menuItems.includes('vimeo')}>Vimeo</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('accreditations')}></span>
                        <a
                            class="menu-item"
                            href="accreditationInfos"
                            ?hidden=${!this.menuItems.includes('accreditations')}>${translate('menuAccreditationInfos')}</a>
                        <span
                            class="separator"
                            ?hidden=${!this.menuItems.includes('accreditations')}></span>
                        <a
                            class="menu-item"
                            href="accreditations"
                            ?hidden=${!this.menuItems.includes('accreditations')}>${translate('menuAccreditations')}</a>
                    </div>
                 </div>
            </div>
            <sdb-layout-main>
                <sdb-embedded-composition-editor
                    .settings=${{ portalIdentifier: 'GLOBAL', env: window.ENVIRONMENT }}>
                </sdb-embedded-composition-editor>
                <sdb-embedded-preview-dialog>
                </sdb-embedded-preview-dialog>
            </sdb-layout-main>
        `;
    }

    firstUpdated() {

        // The embedded editor initialises the environment from .settings; the menu
        // items come from the API and are only needed by this shell.
        store.dispatch(loadUserSettings({ environment: window.ENVIRONMENT }));

        applyStyles(sharedStyles, true);
        applyStyles(colors, true);
        applyStyles(SdbCompositionEditorSdb.themeStyles);
    }
}
